import {View, Text, StyleSheet, TouchableOpacity} from 'react-native';
import {useTranslation} from 'react-i18next';
import {useState, useEffect} from 'react';
import {Globe, Check} from 'lucide-react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {useCurrentTheme} from '@/theming/useCurrentTheme';
import {ThemeType} from '@/theming/baseTheme';
import {SUPPORTED_LANGUAGES, changeLanguage} from '@/lib/i18n';

const LANGUAGE_STORAGE_KEY = '@octahub_language_override';

export function LanguagePicker() {
    const {theme} = useCurrentTheme();
    const {t} = useTranslation();
    const styles = createStyles(theme);
    const [selectedCode, setSelectedCode] = useState<string>('system');

    useEffect(() => {
        AsyncStorage.getItem(LANGUAGE_STORAGE_KEY).then((saved) => {
            setSelectedCode(saved || 'system');
        }).catch(() => {});
    }, []);

    const handleSelect = async (code: string) => {
        setSelectedCode(code);
        await changeLanguage(code);
    };

    return (
        <View style={styles.card}>
            <View style={styles.header}>
                <View style={styles.headerIcon}>
                    <Globe size={20} color={theme.colors.textSecondary} />
                </View>
                <View>
                    <Text style={styles.sectionTitle}>{t('settings.language.title')}</Text>
                    <Text style={styles.sectionSubtitle}>{t('settings.language.subtitle')}</Text>
                </View>
            </View>

            <View style={styles.languageList}>
                {SUPPORTED_LANGUAGES.map((lang) => {
                    const isSelected = lang.code === selectedCode;
                    const displayName = lang.code === 'system'
                        ? t('settings.language.systemDefault')
                        : lang.name;

                    return (
                        <TouchableOpacity
                            key={lang.code}
                            style={[styles.languageRow, isSelected && styles.languageRowSelected]}
                            onPress={() => handleSelect(lang.code)}
                            activeOpacity={0.7}
                        >
                            <Text style={[styles.languageName, isSelected && styles.languageNameSelected]}>
                                {displayName}
                            </Text>
                            {isSelected && (
                                <Check size={18} color={theme.colors.primary} />
                            )}
                        </TouchableOpacity>
                    );
                })}
            </View>
        </View>
    );
}

const createStyles = (theme: ThemeType) =>
    StyleSheet.create({
        card: {
            margin: theme.spacing.md,
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.lg,
            padding: theme.spacing.lg,
            borderWidth: 1,
            borderColor: theme.colors.border,
        },
        header: {
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: theme.spacing.lg,
            gap: theme.spacing.md,
        },
        headerIcon: {
            width: 40,
            height: 40,
            borderRadius: theme.borderRadius.md,
            backgroundColor: theme.colors.background,
            alignItems: 'center',
            justifyContent: 'center',
        },
        sectionTitle: {
            fontSize: theme.fontSize.lg,
            fontWeight: '700',
            color: theme.colors.text,
        },
        sectionSubtitle: {
            fontSize: theme.fontSize.sm,
            color: theme.colors.textSecondary,
            marginTop: 2,
        },
        languageList: {
            gap: 2,
        },
        languageRow: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingVertical: theme.spacing.sm + 4,
            paddingHorizontal: theme.spacing.sm,
            borderRadius: theme.borderRadius.md,
        },
        languageRowSelected: {
            backgroundColor: theme.colors.background,
        },
        languageName: {
            fontSize: theme.fontSize.md,
            color: theme.colors.text,
        },
        languageNameSelected: {
            fontWeight: '600',
        },
    });
